import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Header from '../../components/Header';
import { toast } from 'react-toastify';
import trendingApi from '../../api/trendingApi';
import { LinkSource } from '../../constants/link-source';
import resolveBuyLink, { NO_AFF_LINK_MESSAGE } from '../../utils/resolve-buy-link';
import SavedProductCard from './saved-product-card';
import RecentlyViewedTab from './recently-viewed-tab';

const TABS = [
    { value: 'saved', label: 'Đã lưu', emoji: '❤️', activeClass: 'bg-primary-500 text-white shadow-primary-200' },
    { value: 'recent', label: 'Đã xem gần đây', emoji: '🕐', activeClass: 'bg-orange-500 text-white shadow-orange-200' },
];

function SavedProducts() {
    const navigate = useNavigate();
    const { isLogined } = useSelector((state) => state.auth);

    const [activeTab, setActiveTab] = useState('saved');
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRedirecting, setIsRedirecting] = useState(false);

    const fetchSaved = useCallback(async () => {
        if (!isLogined) return;
        try {
            setIsLoading(true);
            const res = await trendingApi.getSavedProducts({ page: 1, limit: 50 });
            setProducts(res.data?.data?.products || []);
        } catch (err) {
            console.warn('[SavedProducts] fetch error:', err);
        } finally {
            setIsLoading(false);
        }
    }, [isLogined]);

    useEffect(() => {
        fetchSaved();
    }, [fetchSaved]);

    const handleRemove = async (productId) => {
        // Optimistic update
        setProducts((prev) => prev.filter((p) => p.productId !== productId));
        try {
            await trendingApi.unsaveProduct(productId);
        } catch {
            fetchSaved(); // Revert on error
        }
    };

    const handleBuy = async (product) => {
        setIsRedirecting(true);
        try {
            const { url, followUpUrl } = await resolveBuyLink({
                platform: product.platform,
                productLink: product.productLink,
                productId: product.productId,
                meta: {
                    productId: product.productId,
                    productName: product.productName,
                    productImage: product.productImage,
                    productPrice: product.productPrice,
                    shopName: product.shopName,
                    commission: product.commission,
                },
            }, LinkSource.Saved);
            window.location.href = url;
            if (followUpUrl) setTimeout(() => { window.location.href = followUpUrl; }, 2500);
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || NO_AFF_LINK_MESSAGE);
        } finally {
            setIsRedirecting(false);
        }
    };

    const handleShare = (product) => {
        if (navigator.share) {
            navigator.share({ title: product.productName, url: product.productLink }).catch(() => {});
        } else {
            navigator.clipboard?.writeText(product.productLink);
        }
    };

    return (
        <div className="min-h-dvh bg-surface-muted flex flex-col md:w-[768px] lg:w-[1024px] md:mx-auto">
            {isRedirecting && (
                <div className="fixed inset-0 z-50 bg-black/50 flex flex-col items-center justify-center gap-3">
                    <div className="bg-surface rounded-2xl p-6 flex flex-col items-center gap-3 shadow-xl mx-6">
                        <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
                        <p className="text-sm font-semibold text-content-2">Đang tạo link hoàn tiền...</p>
                        <p className="text-xs text-content-3 text-center">Hệ thống đang tạo link để kích hoạt hoàn tiền cho bạn</p>
                    </div>
                </div>
            )}
            <Header hasBackButton={true} />
            <div className="pt-20 flex flex-col flex-1">
                {/* Tab bar — pill style like Trending */}
                <div className="flex gap-2 mx-3 mt-3 px-3 py-2 bg-surface rounded-xl overflow-x-auto scrollbar-hide">
                    {TABS.map((tab) => {
                        const isActive = activeTab === tab.value;
                        return (
                            <button
                                key={tab.value}
                                onClick={() => setActiveTab(tab.value)}
                                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-200 flex-shrink-0 ${
                                    isActive
                                        ? tab.activeClass
                                        : 'bg-surface-muted text-content-3 hover:bg-surface-raised hover:text-content-2'
                                }`}
                            >
                                <span className="text-base leading-none">{tab.emoji}</span>
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {/* Tab content */}
                {activeTab === 'saved' ? (
                    isLoading ? (
                        <div className="flex flex-col gap-3 p-3">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="bg-surface rounded-lg h-28 animate-pulse" />
                            ))}
                        </div>
                    ) : products.length === 0 ? (
                        <div className="text-center py-12 text-content-3 text-sm px-6">
                            Chưa lưu sản phẩm nào.{' '}
                            <button
                                onClick={() => navigate('/trending')}
                                className="text-primary-500 font-semibold underline"
                            >
                                Khám phá xu hướng
                            </button>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-2 p-3">
                            {products.map((product) => (
                                <SavedProductCard
                                    key={`saved-${product.productId}`}
                                    product={product}
                                    onBuy={handleBuy}
                                    onShare={handleShare}
                                    onRemove={handleRemove}
                                />
                            ))}
                        </div>
                    )
                ) : (
                    <RecentlyViewedTab />
                )}
            </div>
        </div>
    );
}

export default SavedProducts;
